/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { ReactNode } from "react";
import { FadeIn } from "./primitives/fade-in";

interface MarketingPageHeroProps {
  badge?: string;
  title: string | ReactNode;
  description: string;
  centered?: boolean;
  className?: string;
  /** "dark" uses bg-primary with grain-texture + blueprint-grid (matches solution heroes) */
  variant?: "light" | "dark";
}

/**
 * Shared hero component for marketing pages
 * Consistent with landing page design system
 */
export function MarketingPageHero({
  badge,
  title,
  description,
  centered = true,
  className = "",
  variant = "light",
}: MarketingPageHeroProps) {
  const isDark = variant === "dark";

  return (
    <section
      className={`relative py-8 md:py-16 lg:py-20 ${
        isDark
          ? "bg-primary text-primary-foreground grain-texture"
          : "bg-background"
      } ${centered ? "text-center" : ""} ${className}`}
      aria-label="Page hero"
    >
      {isDark && (
        <div
          className="blueprint-grid-gold absolute inset-0 opacity-10"
          aria-hidden="true"
        />
      )}

      <div className="relative z-10 max-w-[1280px] mx-auto px-4 md:px-8 lg:px-16">
        <header className={centered ? "max-w-4xl mx-auto" : "max-w-5xl"}>
          {badge && (
            <FadeIn>
              <div className="mb-4">
                <span
                  className={
                    isDark
                      ? "inline-block px-4 py-2 rounded-full bg-white/10 border border-white/20 text-sm font-semibold uppercase tracking-wide"
                      : "section-badge"
                  }
                >
                  {badge}
                </span>
              </div>
            </FadeIn>
          )}

          <FadeIn delay={100}>
            <h1
              className={`mb-4 text-[clamp(36px,8vw,72px)] leading-[1.1] tracking-tight font-bold font-display ${
                isDark ? "text-primary-foreground" : "text-foreground"
              }`}
            >
              {title}
            </h1>
          </FadeIn>

          <FadeIn delay={200}>
            <p
              className={`text-base leading-relaxed max-w-3xl mx-auto ${
                isDark ? "text-primary-foreground/70" : "text-muted-foreground"
              }`}
            >
              {description}
            </p>
          </FadeIn>
        </header>
      </div>
    </section>
  );
}
